import {match} from "@formatjs/intl-localematcher";
import Negotiator from "negotiator";
import {RequestCookies} from "next/dist/server/web/spec-extension/cookies";
import {NextRequest} from "next/server";
import {LOCALE_COOKIE_NAME} from "./setLocale";
import {GetServerSidePropsContext} from "next";
import {DEFAULT_LANG} from "./constants";
import {orderedLanguageCodes} from "../../languages";

type GetServerSidePropsRequest = GetServerSidePropsContext["req"];

type AnyNextRequest = NextRequest | GetServerSidePropsRequest;

function headersAsObject(
  headers: AnyNextRequest["headers"],
): Record<string, string | string[] | undefined> {
  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  } else {
    return headers;
  }
}

function cookiesAsObject(cookies: AnyNextRequest["cookies"]): Record<string, string | undefined> {
  if (cookies instanceof RequestCookies) {
    return {[LOCALE_COOKIE_NAME]: cookies.get(LOCALE_COOKIE_NAME)?.value};
  } else {
    return cookies;
  }
}

export function localeFromCookies(cookies: AnyNextRequest["cookies"]): string | null {
  const locale = cookiesAsObject(cookies)[LOCALE_COOKIE_NAME];
  if (typeof locale === "string") {
    return locale;
  } else {
    return null;
  }
}

function localeFromHeaders(headers: AnyNextRequest["headers"]): string {
  const headersObj = headersAsObject(headers);
  const negotiator = new Negotiator({headers: headersObj});
  const languages = negotiator.languages();
  if (languages.length === 1 && languages[0] == "*") {
    // This happens when the client doesn't send an Accept-Language header.
    // @formatjs/intl-localematcher can't accept the * input, so we need to
    // directly return the default language.
    return DEFAULT_LANG;
  }
  return match(languages, orderedLanguageCodes, DEFAULT_LANG);
}

export default function getLocale(req: AnyNextRequest): string {
  return localeFromCookies(req.cookies) ?? localeFromHeaders(req.headers);
}
